import xs from "xstream/index";
import { push } from "../utils/navigation";
import { BASE_URL, NEW_BASE_URL } from "../config";
import * as ActionTypes from "../actions/types";
import * as actions from "../actions";
import sampleCombine from "xstream/extra/sampleCombine";
import delay from "xstream/extra/delay";
import { jsonApiIncludeRelationships } from "../constants/jsonapi_helpers";
import { getRouteUrl } from "../routers";
import { ROUTE_APPLICATION_VIEW } from "../routers/content/application";
import { generalHeader } from "./headers";
import { ROUTE_ORGANIZATION_VIEW_SINGLE } from "../routers/content/organization";
import v4 from "uuid/v4";
import { createBackendRequest } from "../mappers/backendMappers";

const REQUEST_FETCH_APPLICATIONS = "fetchApplications";
const REQUEST_FETCH_ALLOWED_GRANT_TYPES = "fetchAllowedGrantTypesForCreate";

export function fetchApplications(sources) {
  const state$ = sources.STATE;
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) =>
      action.type === ActionTypes.FETCH_APPLICATIONS ||
      action.type === ActionTypes.DELETE_APPLICATION_SUCCESS ||
      action.type === ActionTypes.CREATE_APPLICATION_SUCCESS ||
      action.type === ActionTypes.EDIT_APPLICATION_SUCCESS ||
      action.type ===
        ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_SUCCESS ||
      action.type === ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_FAILED ||
      action.type === ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_SUCCESS
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => ({
      url: NEW_BASE_URL + "/Application/list",
      category: REQUEST_FETCH_APPLICATIONS,
      method: "POST",
      headers: generalHeader(null),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          pageLimit: 50,
          pageOffset: 0,
          sortField: "created",
          sortOrder: "desc",
        },
      },
      withCredentials: true,
    }));

  const httpResponse$ = sources.HTTP.select(REQUEST_FETCH_APPLICATIONS)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) => {
      return response.status === 200
        ? actions.storeApplications(response.body)
        : actions.fetchApplicationsFailed(response);
    });
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function fetchApplicationsByOrganization(sources) {
  const userId$ = sources.STATE.map((state) => state.applicationUser?.uid);

  // Trigger immediately for normal fetch, with delay for subscription changes
  const normalTrigger$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_APPLICATIONS_BY_ORGANIZATION
  ).compose(sampleCombine(userId$));

  // Add a small delay after subscription changes to ensure backend is updated
  const subscriptionTrigger$ = sources.ACTION.filter(
    (action) =>
      action.type ===
        ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_SUCCESS ||
      action.type === ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_SUCCESS
  )
    .compose(sampleCombine(userId$))
    .compose(delay(500));

  const trigger$ = xs.merge(normalTrigger$, subscriptionTrigger$);

  // Fetch all applications in a single call (no organizationId needed now)
  const applicationsRequest$ = trigger$.map(([action, userId]) => ({
    url: NEW_BASE_URL + "/Application/list",
    category: "fetchAllApplications",
    method: "POST",
    headers: generalHeader(null),
    withCredentials: true,
    send: {
      header: {
        id: v4(),
        application: v4(),
        bank: "NBG",
        userId: userId || v4(),
      },
      payload: {
        pageLimit: 500,
        pageOffset: 0,
        sortField: "created",
        sortOrder: "desc",
        // No organizationId - fetch all applications
      },
    },
  }));

  // Fetch organizations to get their names
  const organizationsRequest$ = trigger$.map(([action, userId]) => ({
    url: NEW_BASE_URL + "/Organization/list",
    category: "fetchOrganizationsForNames",
    method: "POST",
    headers: generalHeader(null),
    withCredentials: true,
    send: {
      header: {
        id: v4(),
        application: v4(),
        bank: "NBG",
        userId: userId || v4(),
      },
      payload: {
        pageLimit: 500,
        pageOffset: 0,
        sortField: "created",
        sortOrder: "desc",
      },
    },
  }));

  // Handle applications response
  const applicationsResponse$ = sources.HTTP.select("fetchAllApplications")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten();

  // Handle organizations response
  const organizationsResponse$ = sources.HTTP.select(
    "fetchOrganizationsForNames"
  )
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten();

  // Combine both responses when they're ready
  const combinedResponse$ = xs
    .combine(applicationsResponse$, organizationsResponse$)
    .map(([appsResponse, orgsResponse]) => {
      // Check if both responses are successful
      if (appsResponse.status === 200 && orgsResponse.status === 200) {
        const applications = appsResponse.body?.payload?.data || [];
        const organizations = orgsResponse.body?.payload?.data || [];

        // Create a map of organizationId to organization name
        const orgMap = {};
        organizations.forEach((org) => {
          orgMap[org.id] = org.orgName || org.title || "Unknown";
        });

        // Map organization names to applications
        const applicationsWithOrgNames = applications.map((app) => ({
          ...app,
          organizationName: orgMap[app.organizationId] || "Unknown",
          // Keep the organizationId as well
        }));

        return actions.fetchApplicationsByOrganizationSuccess({
          payload: {
            payload: {
              data: applicationsWithOrgNames,
            },
          },
        });
      } else if (appsResponse.status !== 200) {
        // Applications fetch failed
        return actions.fetchApplicationsByOrganizationFailed(appsResponse);
      } else {
        // Organizations fetch failed, but we can still show apps without org names
        const applications = appsResponse.body?.payload?.data || [];
        const applicationsWithUnknownOrg = applications.map((app) => ({
          ...app,
          organizationName: "Unknown",
        }));

        return actions.fetchApplicationsByOrganizationSuccess({
          payload: {
            payload: {
              data: applicationsWithUnknownOrg,
            },
          },
        });
      }
    });

  const http$ = xs.merge(applicationsRequest$, organizationsRequest$);

  return {
    ACTION: combinedResponse$,
    HTTP: http$,
  };
}

export function createApplication(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  // First, we need to fetch grant types when CREATE_APPLICATION is triggered
  const fetchGrantTypesRequest$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.CREATE_APPLICATION
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => ({
      url: NEW_BASE_URL + "/AllowedGrantType/list",
      category: REQUEST_FETCH_ALLOWED_GRANT_TYPES,
      method: "POST",
      headers: generalHeader(null),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          pageLimit: 50,
          pageOffset: 0,
          sortField: "name",
          sortOrder: "asc",
        },
      },
      withCredentials: true,
      meta: action.payload, // Pass the original action payload
    }));

  // When grant types are fetched, proceed with creating the application
  const createApplicationRequest$ = sources.HTTP.select(
    REQUEST_FETCH_ALLOWED_GRANT_TYPES
  )
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .compose(sampleCombine(csrfToken$, userId$, state$))
    .map(([response, csrfToken, userId, state]) => {
      // Extract the original action payload from meta
      const actionPayload = response.request.meta;
      const organizationId = actionPayload.organizationId;

      // Initialize grant type mapping
      const allGrantTypesWithIds = new Map();

      // First, add grant types from the API response
      if (response.status === 200 && response.body?.payload?.data) {
        const grantTypes = response.body.payload.data;
        grantTypes.forEach((grant) => {
          // The grant type might have different field names, let's check
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            // Store by lowercase name to handle case variations
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      } else if (response.status === 200 && response.body?.payload) {
        // Maybe the data is directly in payload without the data field
        const grantTypes = Array.isArray(response.body.payload)
          ? response.body.payload
          : [response.body.payload];
        grantTypes.forEach((grant) => {
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      }

      // Fallback: Try to get grant types from all applications in the organization
      if (
        allGrantTypesWithIds.size === 0 &&
        organizationId &&
        state.organizationReducer?.byId?.[organizationId]
      ) {
        const org = state.organizationReducer.byId[organizationId];

        // Check if organization has applications with grant types
        if (org.applications && Array.isArray(org.applications)) {
          org.applications.forEach((app) => {
            if (
              app.allowedGrantTypesWithIds &&
              Array.isArray(app.allowedGrantTypesWithIds)
            ) {
              app.allowedGrantTypesWithIds.forEach((grant) => {
                if (grant && grant.name && grant.id) {
                  const key = grant.name.toLowerCase();
                  if (!allGrantTypesWithIds.has(key)) {
                    allGrantTypesWithIds.set(key, grant);
                  }
                }
              });
            }
          });
        }
      }

      // Also check all applications in the application state as a last resort
      if (
        allGrantTypesWithIds.size === 0 &&
        state.application?.applicationsById
      ) {
        Object.values(state.application.applicationsById).forEach((app) => {
          if (
            app &&
            app.allowedGrantTypesWithIds &&
            Array.isArray(app.allowedGrantTypesWithIds)
          ) {
            app.allowedGrantTypesWithIds.forEach((grant) => {
              if (grant && grant.name && grant.id) {
                const key = grant.name.toLowerCase();
                if (!allGrantTypesWithIds.has(key)) {
                  allGrantTypesWithIds.set(key, grant);
                }
              }
            });
          }
        });
      }

      // Create the final mapping
      const grantTypeMapping = {};
      const grantTypeMappingLower = {};

      allGrantTypesWithIds.forEach((grant, key) => {
        grantTypeMapping[grant.name] = grant.id;
        grantTypeMappingLower[key] = grant.id;
      });

      // Map the selected grant types to their IDs
      const selectedGrantTypes = actionPayload.allowedGrantTypes || [];
      const allowedGrantTypes = selectedGrantTypes
        .map((grantType) => {
          // Try exact match first
          let id = grantTypeMapping[grantType];

          // If no exact match, try case-insensitive match
          if (!id) {
            id = grantTypeMappingLower[grantType.toLowerCase()];
          }

          return id;
        })
        .filter(Boolean); // Remove any undefined mappings

      return {
        url: NEW_BASE_URL + "/Organization/application/create",
        category: "createApplication",
        method: "POST",
        headers: generalHeader(csrfToken),
        send: {
          header: {
            id: v4(),
            application: v4(),
            bank: "NBG",
            userId: userId || v4(),
          },
          payload: {
            createApplication: {
              appName: actionPayload.title,
              description: actionPayload.description || "",
              allowedGrantTypes: allowedGrantTypes, // Now using mapped IDs
              oauthRedirectUri: actionPayload.oAuthRedirectUri
                ? actionPayload.oAuthRedirectUri.join(",")
                : "",
              postlogoutRedirectUri: actionPayload.postLogoutRedirectUri
                ? actionPayload.postLogoutRedirectUri.join(",")
                : "",
              langcode: "en",
              status: true,
              published: true,
            },
            organizationId: actionPayload.organizationId,
          },
        },
        withCredentials: true,
      };
    });

  const request$ = xs.merge(fetchGrantTypesRequest$, createApplicationRequest$);

  let httpResponse$ = sources.HTTP.select("createApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) => {
      // Create a response structure compatible with existing code
      const responseBody = {
        data: {
          id: response.body.payload,
        },
      };
      return actions.createApplicationSuccess(responseBody);
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function createApplicationFailed(sources) {
  const response$ = sources.HTTP.select("createApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.createApplicationFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    // For other errors, pass the response as is
    return actions.createApplicationFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function createTerminalApplication(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  // First, fetch grant types when CREATE_TERMINAL_APPLICATION is triggered
  const fetchGrantTypesRequest$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.CREATE_TERMINAL_APPLICATION
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => ({
      url: NEW_BASE_URL + "/AllowedGrantType/list",
      category: "fetchAllowedGrantTypesForTerminal",
      method: "POST",
      headers: generalHeader(null),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          pageLimit: 50,
          pageOffset: 0,
          sortField: "name",
          sortOrder: "asc",
        },
      },
      withCredentials: true,
      meta: action.payload, // Pass the original action payload
    }));

  // When grant types are fetched, proceed with creating the application
  const createApplicationRequest$ = sources.HTTP.select(
    "fetchAllowedGrantTypesForTerminal"
  )
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .compose(sampleCombine(csrfToken$, userId$, state$))
    .map(([response, csrfToken, userId, state]) => {
      // Extract the original action payload from meta
      const actionPayload = response.request.meta;
      const organizationId = actionPayload.organization;

      // Initialize grant type mapping
      const allGrantTypesWithIds = new Map();

      // First, add grant types from the API response
      if (response.status === 200 && response.body?.payload?.data) {
        const grantTypes = response.body.payload.data;
        grantTypes.forEach((grant) => {
          // The grant type might have different field names, let's check
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            // Store by lowercase name to handle case variations
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      } else if (response.status === 200 && response.body?.payload) {
        // Maybe the data is directly in payload without the data field
        const grantTypes = Array.isArray(response.body.payload)
          ? response.body.payload
          : [response.body.payload];
        grantTypes.forEach((grant) => {
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      }

      // Fallback: Try to get grant types from all applications in the organization
      if (
        allGrantTypesWithIds.size === 0 &&
        organizationId &&
        state.organizationReducer?.byId?.[organizationId]
      ) {
        const org = state.organizationReducer.byId[organizationId];

        // Check if organization has applications with grant types
        if (org.applications && Array.isArray(org.applications)) {
          org.applications.forEach((app) => {
            if (
              app.allowedGrantTypesWithIds &&
              Array.isArray(app.allowedGrantTypesWithIds)
            ) {
              app.allowedGrantTypesWithIds.forEach((grant) => {
                if (grant && grant.name && grant.id) {
                  const key = grant.name.toLowerCase();
                  if (!allGrantTypesWithIds.has(key)) {
                    allGrantTypesWithIds.set(key, grant);
                  }
                }
              });
            }
          });
        }
      }

      // Also check all applications in the application state as a last resort
      if (
        allGrantTypesWithIds.size === 0 &&
        state.application?.applicationsById
      ) {
        Object.values(state.application.applicationsById).forEach((app) => {
          if (
            app &&
            app.allowedGrantTypesWithIds &&
            Array.isArray(app.allowedGrantTypesWithIds)
          ) {
            app.allowedGrantTypesWithIds.forEach((grant) => {
              if (grant && grant.name && grant.id) {
                const key = grant.name.toLowerCase();
                if (!allGrantTypesWithIds.has(key)) {
                  allGrantTypesWithIds.set(key, grant);
                }
              }
            });
          }
        });
      }

      // Create the final mapping
      const grantTypeMapping = {};
      const grantTypeMappingLower = {};

      allGrantTypesWithIds.forEach((grant, key) => {
        grantTypeMapping[grant.name] = grant.id;
        grantTypeMappingLower[key] = grant.id;
      });

      // Map the selected grant types to their IDs
      const selectedGrantTypes = actionPayload.allowedGrantTypes || [];
      const allowedGrantTypes = selectedGrantTypes
        .map((grantType) => {
          // Try exact match first
          let id = grantTypeMapping[grantType];

          // If no exact match, try case-insensitive match
          if (!id) {
            id = grantTypeMappingLower[grantType.toLowerCase()];
          }

          return id;
        })
        .filter(Boolean); // Remove any undefined mappings

      return {
        url: NEW_BASE_URL + "/Organization/application/create",
        category: "createTerminalApplication",
        method: "POST",
        headers: generalHeader(csrfToken),
        send: {
          header: {
            id: v4(),
            application: v4(),
            bank: "NBG",
            userId: userId || v4(),
          },
          payload: {
            createApplication: {
              appName: actionPayload.title,
              description: actionPayload.description || "",
              allowedGrantTypes: allowedGrantTypes, // Now using mapped IDs
              oauthRedirectUri: actionPayload.oAuthRedirectUri
                ? actionPayload.oAuthRedirectUri.join(",")
                : "",
              postlogoutRedirectUri: actionPayload.postLogoutRedirectUri
                ? actionPayload.postLogoutRedirectUri.join(",")
                : "",
              langcode: "en",
              status: true,
              published: true,
            },
            organizationId: organizationId,
          },
        },
        withCredentials: true,
      };
    });

  const request$ = xs.merge(fetchGrantTypesRequest$, createApplicationRequest$);

  let httpResponse$ = sources.HTTP.select("createTerminalApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) => {
      // Create a response structure compatible with existing code
      const responseBody = {
        data: {
          id: response.body.payload,
          organizationId: response.request.send.payload.organizationId,
        },
      };
      return actions.createTerminalApplicationSuccess(responseBody);
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function createTerminalApplicationFailed(sources) {
  const response$ = sources.HTTP.select("createTerminalApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.createTerminalApplicationFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    // For other errors, pass the response as is
    return actions.createTerminalApplicationFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function redirectAfterCreatingApplication(sources) {
  let httpResponse$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.CREATE_APPLICATION_SUCCESS
  ).map((action) => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get("organization");
    return push(
      getRouteUrl(
        ROUTE_APPLICATION_VIEW,
        {
          applicationId: action.payload.data.id,
        },
        { organization: query }
      )
    );
  });

  return {
    ACTION: httpResponse$,
  };
}

export function redirectAfterCreatingTerminalApplication(sources) {
  let httpResponse$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.CREATE_TERMINAL_APPLICATION_SUCCESS
  )
    .filter((action) => {
      // Ensure we have the required data for redirect
      return action.payload?.data?.id && action.payload?.data?.organizationId;
    })
    .map((action) => {
      const organizationId = action.payload.data.organizationId;
      const applicationId = action.payload.data.id;
      const url = getRouteUrl(
        ROUTE_APPLICATION_VIEW,
        {
          applicationId: applicationId,
        },
        { organization: organizationId }
      );
      return push(url);
    });

  return {
    ACTION: httpResponse$,
  };
}

export function clearStateAfterCreatingApplication(sources) {
  let httpResponse$ = sources.HTTP.select("createApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Only clear state if it's a successful 200 response without exception
      return response.status === 200 && !response.body?.exception;
    })
    .map(() => actions.clearStateAfterCreatingApplication());

  return {
    ACTION: httpResponse$,
  };
}

export function editApplication(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  // First, fetch grant types when EDIT_APPLICATION is triggered
  const fetchGrantTypesRequest$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.EDIT_APPLICATION
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => ({
      url: NEW_BASE_URL + "/AllowedGrantType/list",
      category: "fetchAllowedGrantTypesForEdit",
      method: "POST",
      headers: generalHeader(null),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          pageLimit: 50,
          pageOffset: 0,
          sortField: "name",
          sortOrder: "asc",
        },
      },
      withCredentials: true,
      meta: action.payload, // Pass the original action payload
    }));

  // When grant types are fetched, proceed with editing the application
  const editApplicationRequest$ = sources.HTTP.select(
    "fetchAllowedGrantTypesForEdit"
  )
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .compose(sampleCombine(csrfToken$, userId$, state$))
    .map(([response, csrfToken, userId, state]) => {
      // Extract the original action payload from meta
      const action = { payload: response.request.meta };
      const applicationUuid = action.payload.applicationUuid;
      const organizationId = action.payload.organizationId;

      // Initialize grant type mapping
      const allGrantTypesWithIds = new Map();

      // First, add grant types from the API response
      if (response.status === 200 && response.body?.payload?.data) {
        const grantTypes = response.body.payload.data;
        grantTypes.forEach((grant) => {
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      } else if (response.status === 200 && response.body?.payload) {
        const grantTypes = Array.isArray(response.body.payload)
          ? response.body.payload
          : [response.body.payload];
        grantTypes.forEach((grant) => {
          const grantName = grant.name || grant.title || grant.grantType;
          const grantId = grant.id || grant.uuid;

          if (grantName && grantId) {
            const key = grantName.toLowerCase();
            allGrantTypesWithIds.set(key, { name: grantName, id: grantId });
          }
        });
      }

      // Fallback: If API didn't return grant types, try to get from existing applications
      if (allGrantTypesWithIds.size === 0) {
        // Get grant types from the current application
        let storedGrantTypesWithIds =
          state.applicationForm?.form?.allowedGrantTypesWithIds || [];

        if (!storedGrantTypesWithIds.length && applicationUuid) {
          const application =
            state.application?.applicationsById?.[applicationUuid];
          if (application) {
            storedGrantTypesWithIds =
              application.allowedGrantTypesWithIds || [];
          }
        }

        storedGrantTypesWithIds.forEach((grant) => {
          if (grant && grant.name && grant.id) {
            allGrantTypesWithIds.set(grant.name.toLowerCase(), grant);
          }
        });

        // Try to get grant types from all applications in the organization
        if (
          organizationId &&
          state.organizationReducer?.byId?.[organizationId]
        ) {
          const org = state.organizationReducer.byId[organizationId];
          if (org.applications && Array.isArray(org.applications)) {
            org.applications.forEach((app) => {
              if (
                app.allowedGrantTypesWithIds &&
                Array.isArray(app.allowedGrantTypesWithIds)
              ) {
                app.allowedGrantTypesWithIds.forEach((grant) => {
                  if (grant && grant.name && grant.id) {
                    const key = grant.name.toLowerCase();
                    if (!allGrantTypesWithIds.has(key)) {
                      allGrantTypesWithIds.set(key, grant);
                    }
                  }
                });
              }
            });
          }
        }

        // Also check all applications in the application state
        if (state.application?.applicationsById) {
          Object.values(state.application.applicationsById).forEach((app) => {
            if (
              app &&
              app.allowedGrantTypesWithIds &&
              Array.isArray(app.allowedGrantTypesWithIds)
            ) {
              app.allowedGrantTypesWithIds.forEach((grant) => {
                if (grant && grant.name && grant.id) {
                  const key = grant.name.toLowerCase();
                  if (!allGrantTypesWithIds.has(key)) {
                    allGrantTypesWithIds.set(key, grant);
                  }
                }
              });
            }
          });
        }
      }

      // Create the final mapping
      const grantTypeMapping = {};
      const grantTypeMappingLower = {};

      allGrantTypesWithIds.forEach((grant, key) => {
        grantTypeMapping[grant.name] = grant.id;
        grantTypeMappingLower[key] = grant.id;
      });

      // Map the selected grant types to their IDs
      const selectedGrantTypes = action.payload.allowedGrantTypes || [];
      const allowedGrantTypes = selectedGrantTypes
        .map((grantType) => {
          // Try exact match first
          let id = grantTypeMapping[grantType];

          // If no exact match, try case-insensitive match
          if (!id) {
            id = grantTypeMappingLower[grantType.toLowerCase()];
          }

          return id;
        })
        .filter(Boolean); // Remove any undefined mappings

      // Ensure subscriptionIds is an array of strings/IDs
      const subscriptionIds = (action.payload.subscriptionIds || [])
        .map((sub) => {
          // If it's an object with an id property, extract the id
          if (typeof sub === "object" && sub !== null && sub.id) {
            return sub.id;
          }
          // Otherwise assume it's already an ID string
          return sub;
        })
        .filter(Boolean);

      return {
        url: NEW_BASE_URL + "/Organization/application/update",
        category: "editApplication",
        method: "POST",
        headers: generalHeader(csrfToken),
        send: {
          header: {
            id: v4(),
            application: v4(),
            bank: "NBG",
            userId: userId || v4(),
          },
          payload: {
            organizationId: action.payload.organizationId,
            applicationId: action.payload.applicationUuid,
            appName: action.payload.title,
            description: action.payload.description,
            allowedGrantTypes: allowedGrantTypes,
            products: subscriptionIds, // Send subscription IDs as products
            oauthRedirectUri: action.payload.oAuthRedirectUri
              ? action.payload.oAuthRedirectUri.join(",")
              : "",
            postlogoutRedirectUri: action.payload.postLogoutRedirectUri
              ? action.payload.postLogoutRedirectUri.join(",")
              : "",
            langcode: "en",
            // Add other fields with default values if needed
            status: true,
            published: true,
          },
        },
        withCredentials: true,
      };
    });

  let httpResponse$ = sources.HTTP.select("editApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      // Check if the response contains an exception (error)
      if (response.body?.exception) {
        // Return failed action with the error details
        return actions.editApplicationFailed({
          status: 200,
          body: response.body,
          error: response.body.exception,
        });
      }

      // Create a response structure compatible with existing code
      const responseBody = {
        data: {
          id:
            response.body.payload ||
            response.request.send.payload.applicationId,
        },
      };
      return actions.editApplicationSuccess(responseBody);
    });

  return {
    ACTION: httpResponse$,
    HTTP: xs.merge(fetchGrantTypesRequest$, editApplicationRequest$),
  };
}

export function editApplicationFailed(sources) {
  const response$ = sources.HTTP.select("editApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.editApplicationFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    // For other errors, pass the response as is
    return actions.editApplicationFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function redirectAfterEditingApplication(sources) {
  let httpResponse$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.EDIT_APPLICATION_SUCCESS
  ).map((action) => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get("organization");
    return push(
      getRouteUrl(
        ROUTE_APPLICATION_VIEW,
        {
          applicationId: action.payload.data.id,
        },
        { organization: query }
      )
    );
  });

  return {
    ACTION: httpResponse$,
  };
}

export function clearStateAfterEditingApplication(sources) {
  let httpResponse$ = sources.HTTP.select("editApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Only clear state if it's a successful 200 response without exception
      return response.status === 200 && !response.body?.exception;
    })
    .map(() => actions.clearStateAfterCreatingApplication());

  return {
    ACTION: httpResponse$,
  };
}

export function fetchApplication(sources) {
  const state$ = sources.STATE;

  // First check if we need to fetch the organization
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_APPLICATION
  )
    .compose(sampleCombine(state$))
    .map(([action, state]) => {
      const { applicationId, organizationId } = action.payload;
      const userId = state.applicationUser?.uid;

      // If organizationId is provided, fetch the organization which includes applications
      if (organizationId) {
        const requestObj = {
          url: NEW_BASE_URL + "/Organization/get",
          category: "fetchApplicationViaOrg",
          method: "POST",
          headers: generalHeader(null),
          withCredentials: true,
          send: {
            header: {
              id: v4(),
              application: v4(),
              bank: "NBG",
              userId: userId || v4(),
            },
            payload: {
              id: organizationId,
              title: null,
            },
          },
          meta: { applicationId }, // Pass applicationId in meta for later use
        };
        return requestObj;
      }

      // Fallback to fetching all applications and filtering
      // This is used when organizationId is not available
      return {
        url: NEW_BASE_URL + "/Application/list",
        category: "fetchApplicationViaList",
        method: "POST",
        headers: generalHeader(null),
        withCredentials: true,
        send: {
          header: {
            id: v4(),
            application: v4(),
            bank: "NBG",
            userId: userId || v4(),
          },
          payload: {
            pageLimit: 50,
            pageOffset: 0,
            sortField: "created",
            sortOrder: "desc",
          },
        },
        meta: { applicationId }, // Pass applicationId in meta for filtering
      };
    });

  // Handle organization response and extract application
  const orgResponse$ = sources.HTTP.select("fetchApplicationViaOrg")
    .map((response) =>
      response.replaceError((err) => {
        return xs.of(err);
      })
    )
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      const applicationId = response.request.meta?.applicationId;

      // The response might have the org directly in payload, not in data
      let org = null;
      if (response.body?.payload) {
        // Check if data exists and is an array
        if (Array.isArray(response.body.payload.data)) {
          org = response.body.payload.data[0]; // Get first org from array
        } else if (response.body.payload.data) {
          org = response.body.payload.data; // Direct object
        } else {
          // Maybe the org is directly in payload (not in a data field)
          org = response.body.payload;
        }
      }

      if (org) {
        // Find the application in the organization's applications
        const app = org?.applications?.find(
          (a) => a.id === applicationId || a.uuid === applicationId
        );

        if (app) {
          // Return both actions - organization and application as a stream
          return xs.of(
            actions.fetchOrganizationsSuccess(response.body),
            actions.storeApplication({
              payload: {
                data: [app], // Wrap in array as the mapper expects
              },
            })
          );
        }
      }

      return xs.empty();
    })
    .flatten(); // Flatten the stream of actions

  // Handle list response (when fetching without organizationId)
  const listResponse$ = sources.HTTP.select("fetchApplicationViaList")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      const applicationId = response.request.meta?.applicationId;
      const applications = response.body?.payload?.data || [];

      // Find the specific application by ID
      const app = applications.find(
        (a) => a.id === applicationId || a.uuid === applicationId
      );

      if (app) {
        return actions.storeApplication({
          payload: {
            data: [app], // Wrap in array as the mapper expects
          },
        });
      }

      // Return empty if application not found
      return actions.fetchApplicationFailed({
        message: "Application not found",
      });
    });

  const combinedAction$ = xs.merge(orgResponse$, listResponse$);

  return {
    ACTION: combinedAction$,
    HTTP: request$,
  };
}

export function fetchApplicationFailed(sources) {
  // Handle failures from both organization and list endpoints
  const orgFailed$ = sources.HTTP.select("fetchApplicationViaOrg")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status !== 200);

  const listFailed$ = sources.HTTP.select("fetchApplicationViaList")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status !== 200);

  const response$ = xs.merge(orgFailed$, listFailed$);

  const action$ = response$.map((response) => {
    // Handle cases where response.response might be undefined
    const errorData = response.response?.text
      ? JSON.parse(response.response.text)
      : { message: "Failed to fetch application" };
    return actions.fetchApplicationFailed(errorData);
  });

  return {
    ACTION: action$,
  };
}

export function deleteApplication(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.DELETE_APPLICATION
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => ({
      url: NEW_BASE_URL + "/Organization/application/delete",
      category: "deleteApplication",
      method: "POST",
      headers: generalHeader(csrfToken),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          applicationId: action.payload.applicationId,
          organizationId: action.payload.organizationId,
        },
      },
      withCredentials: true,
    }));

  const httpResponse$ = sources.HTTP.select("deleteApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) => actions.deleteApplicationSuccess(response));

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function redirectAfterDeleteApplicationSuccess(sources) {
  const organizationId$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.DELETE_APPLICATION
  ).map((action) => action.payload.organizationId);

  const httpResponse$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.DELETE_APPLICATION_SUCCESS
  )
    .compose(sampleCombine(organizationId$))
    .map(([action, organizationId]) =>
      push(
        getRouteUrl(ROUTE_ORGANIZATION_VIEW_SINGLE, {
          organizationId: organizationId,
        })
      )
    );

  return {
    ACTION: httpResponse$,
  };
}

export function deleteApplicationFailed(sources) {
  const response$ = sources.HTTP.select("deleteApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.deleteApplicationFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    // For other errors, pass the response as is
    return actions.deleteApplicationFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function unsubscribeFromApiProduct(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  let request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => {
      // Create request body for new backend API
      const requestBody = createBackendRequest(
        {
          ApplicationId: action.payload.planUuid,
          ProductId: action.payload.apiProductUuid,
        },
        userId
      );

      return {
        url: NEW_BASE_URL + "/AppSubscription/delete",
        category: "unsubscribeApiProductFromApplication",
        method: "POST",
        headers: generalHeader(csrfToken),
        send: requestBody,
        withCredentials: true,
      };
    });

  let httpResponse$ = sources.HTTP.select(
    "unsubscribeApiProductFromApplication"
  )
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) => actions.unsubscribeFromApiProductSuccess(response));

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function unsubscribeFromApiProductFailed(sources) {
  const response$ = sources.HTTP.select("unsubscribeApiProductFromApplication")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => {
      // Handle both non-200 status and 200 status with exception
      return response.status !== 200 || response.body?.exception;
    });

  const action$ = response$.map((response) => {
    // If it's a 200 with exception, include the error details
    if (response.status === 200 && response.body?.exception) {
      return actions.unsubscribeFromApiProductFailed({
        status: 200,
        body: response.body,
        error: response.body.exception,
      });
    }
    return actions.unsubscribeFromApiProductFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function resetApplicationClientSecret(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const userId$ = state$.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.RESET_APPLICATION_CLIENT_SECRET
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => ({
      //eslint-disable-line
      url: NEW_BASE_URL + "/Application/reset-secret",
      category: "resetApplicationClientSecret",
      method: "POST",
      headers: generalHeader(csrfToken),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          applicationId: action.payload.applicationId,
          isProduction: action.payload.environment === "PRODUCTION",
        },
      },
      withCredentials: true,
    }));

  let httpResponse$ = sources.HTTP.select("resetApplicationClientSecret")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200 && !response.body?.exception)
    .map((response) =>
      actions.resetApplicationClientSecretSuccess(
        response.body?.payload || response.body
      )
    );

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function resetApplicationClientSecretFailed(sources) {
  const response$ = sources.HTTP.select("resetApplicationClientSecret")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status !== 200 || response.body?.exception);

  const action$ = response$.map((response) => {
    // Handle exception in response body
    if (response.body?.exception) {
      return actions.resetApplicationClientSecretFailed({
        error: response.body.exception,
        status: response.status,
      });
    }
    return actions.resetApplicationClientSecretFailed(response);
  });

  return {
    ACTION: action$,
  };
}

export function deleteClientSecret(sources) {
  const state$ = sources.STATE;
  const userId$ = state$.map((state) => state.applicationUser?.uid);
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.DELETE_APPLICATION_CLIENT_SECRET
  )
    .compose(sampleCombine(userId$, csrfToken$))
    .map(([action, userId, csrfToken]) => ({
      url: NEW_BASE_URL + `/Application/delete-secret`,
      category: "deleteApplicationClientSecret",
      method: "POST",
      headers: generalHeader(csrfToken),
      send: {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          applicationId: action.payload,
        },
      },
      withCredentials: true,
    }));

  return {
    HTTP: request$,
  };
}

export function validatePsdCertificate(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.VALIDATE_PSD_CERTIFICATE
  )
    .compose(sampleCombine(csrfToken$))
    .map(([action, csrfToken]) => ({
      url:
        BASE_URL +
        `/phpsdk/certificates/${action.payload.applicationId}/validate?_format=json`,
      category: "validatePsdCertificate",
      method: "GET",
      headers: generalHeader(csrfToken, true),
      withCredentials: true,
    }));

  let httpResponse$ = sources.HTTP.select("validatePsdCertificate")
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) => {
      return response.status === 200
        ? actions.validatePsdCertificateSuccess(response.body)
        : actions.validatePsdCertificateFailed(response);
    });
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}
